import type { CardProps } from '@/scopes/prices/components/TransportSelector/Card'
import { airlineCodesMap, AirlineIcon } from '@sunwebgroup/uds-ui/airline-icon'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { identity } from 'ramda'
import { useIntl } from '@/intl'
import { LuggageType } from '@/scopes/prices/domain/transports/types'
import styles from './FlightCard.module.css'

export function FlightCard({
  id,
  arrivalDate,
  departureDate,
  route,
  airline,
  luggage,
  flightType,
  averagePrice,
  isSelected,
  remark,
  setSelected = identity,
  noActiveState,
}: CardProps) {
  const intl = useIntl()

  const flightTypeLabel
    = flightType === 'standard'
      ? 'prices_transport_selector_flight_type_info_standard_value'
      : 'prices_transport_selector_flight_type_info_flexible_value'
  const isClickable = !isSelected && !noActiveState
  const selectCard = () => isClickable && setSelected(id)

  return (
    <div
      className={cn(styles.listItemRoot, isSelected && !noActiveState && styles.listItemRootSelected)}
      onClick={selectCard}
      role="presentation"
    >
      <div className={styles.cardContent}>
        <div className={styles.listItemContent}>
          <Typography variant="body-l-bold-tight">{`${route.from.name} ${route.from.code} - ${route.to.name} ${route.to.code}`}</Typography>
          <Typography variant="body-m-regular-tight">{`${departureDate.time} - ${arrivalDate.time}`}</Typography>
          <div className={styles.featuresWrapper}>
            <div className={styles.infoBlock}>
              {airline && (
                <div className={styles.airlineWrapper}>
                  <AirlineIcon className={styles.airlineIcon} glyph={airlineCodesMap[airline.id]} size="md" />
                  <Typography variant="body-s-regular-tight">{airline.name}</Typography>
                </div>
              )}
              {luggage && flightType && luggage.map(luggageItem => (
                <div className={styles.infoItem} key={luggageItem.type}>
                  <Icon className={styles.icon} glyph={luggageItem.type === LuggageType.Hold ? 'LuggageCheck' : 'BackpackWithCheckMark'} />
                  <Typography variant="body-s-regular-tight">
                    {luggageItem.type === LuggageType.Hold && `${luggageItem.weight} kg`}
                    {luggageItem.type === LuggageType.Hand
                    && (luggageItem.weight > 0
                      ? `${luggageItem.weight} kg`
                      : intl.formatMessage({ id: 'prices_transport_selector_no-hand-luggage' }))}
                  </Typography>
                </div>
              ))}
              <div className={styles.infoItem}>
                <Icon className={styles.icon} glyph={`${flightType === 'standard' ? 'AirplaneToTop' : 'AirplaneWithArrowsCirclePath'}`} />
                <Typography variant="body-s-regular-tight">{intl.formatMessage({ id: flightTypeLabel })}</Typography>
              </div>
            </div>
            {remark && <Typography as="div" className={styles.remark}>{remark}</Typography>}
          </div>
        </div>
      </div>
      <div className={cn(styles.priceWrapper, isSelected && !noActiveState && styles.priceWrapperSelected)}>
        <>
          <Typography variant="body-m-bold-tight">{intl.formatCurrency(averagePrice, { showSign: true })}</Typography>
          <Typography variant="body-m-bold-tight">
            {' '}
            {intl.formatMessage({ id: 'prices_transport_selector_price-per-person_abbreviation' })}
          </Typography>
        </>
        { !isSelected && (
          <Button size="S" variant="secondary">
            {intl.formatMessage({
              id: 'prices_transport_selector_flight-selector-select',
            })}
          </Button>
        )}
      </div>
    </div>
  )
}
